<template>
  <div>
    <highcharts
      ref="highcharts"
      :options="histogramOptions"
      :callback="chartLoaded"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { isEmpty } from 'lodash';
import {
  timeIntervalToValueMapping,
  findTimeInterval,
  HISTOGRAM_POINTS_LIMIT,
} from '@/data/getDateRanges';

const MAX_TICK_POSITIONS_TO_SHOW = 13;

function convertAggregatedDataToSeriesData(
  data,
  aggregatedBy,
  useEpoch,
  momentTimezoneId,
  yValue
) {
  let series = [];
  let timeInterval = findTimeInterval('datesByYearMonthDayHourMinute');

  data.forEach(function processData(dataPoint) {
    let date = dataPoint[aggregatedBy];
    if (date) {
      console.log('useEpoch', useEpoch);
      if (!useEpoch) {
        if (momentTimezoneId) {
          date = moment
            .utc(date)
            .tz(momentTimezoneId)
            .format(timeIntervalToValueMapping[timeInterval].format);
        } else {
          date = moment
            .utc(date)
            .format(timeIntervalToValueMapping[timeInterval].format);
        }
      }

      let data = {
        y: dataPoint[yValue],
      };

      if (useEpoch) {
        data.x = date;
      } else {
        data.name = date;
      }

      series.push(data);
    }
  });
  const sortedSeries = series.sort(
    (a, b) => new Date(a.name) - new Date(b.name)
  );
  return sortedSeries;
}


export default {
  name: 'HighchartsCallHistogram',
  props: {
    data: {
      type: Array,
      default() {
        return [];
      },
    },
    momentTimezoneId: {
      type: String,
      default: 'America/Los_Angeles',
    },
    groupTimeInterval: {
      type: String,
      default: 'auto',
    },
    computedTimeGroupInterval: {
      type: String,
      default: 'time',
    },
    showLoader: {
      type: Boolean,
      default: false,
    },
    timeRange: {
      type: Array,
      default() {
        return [];
      },
    },
  },

  data() {
    return {
      chart: null,
      columnOptions: {},
    };
  },

  computed: {

    readableTimeInterval() {
      return findTimeInterval(this.computedTimeGroupInterval);
    },

    useEpoch() {
      return (
        this.computedTimeGroupInterval ===
        timeIntervalToValueMapping['minute'].code
      );
    },


    histogramOptions() {
      let series = [
        {
          id: 'Checked',
          name: 'Checked',
          type: 'column',
          data: convertAggregatedDataToSeriesData(
            this.data,
            this.computedTimeGroupInterval,
            this.useEpoch,
            this.momentTimezoneId,
            'numbersChecked'
          ),
          yAxis: 'call-count-axis',
          // pointPlacement: 0.09,
          turboThreshold: HISTOGRAM_POINTS_LIMIT,
          maxPointWidth: 80,
        },
        {
          id: 'Scrubbed',
          name: 'Scrubbed',
          type: 'column',
          data: convertAggregatedDataToSeriesData(
            this.data,
            this.computedTimeGroupInterval,
            this.useEpoch,
            this.momentTimezoneId,
            'numbersScrubed'
          ),
          yAxis: 'call-count-axis',
          // pointPlacement: 0.09,
          turboThreshold: HISTOGRAM_POINTS_LIMIT,
          maxPointWidth: 80,
        },
      ];

      let useEpoch = this.useEpoch;

      return {
        series,
        title: {
          text: 'TCPA Shield Uses in the last 30 days',
        },
        loading: {
          style: {
            backgroundColor: 'var(--reset-body-bg-color)',
            opacity: 0.8,
          },
        },
        chart: {
          height: '10%',
          showDragPoints: {
            enable: this.useEpoch,
            offsetInMinutes: moment.tz(this.momentTimezoneId).utcOffset(),
          },
        },

        tooltip: {
          shared: true,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          borderColor: 'rgba(0, 0, 0, 0.9)',
          style: {
            color: '#fff',
          },
        },
        legend: {
          enabled: false,
        },
        xAxis: [
          {
            crosshair: true,
            type: this.useEpoch ? 'datetime' : 'category',
            id: 'date-axis',
            labels: {
              style: {
                whiteSpace: 'nowrap',
                color: 'var(--reset-body-font-color) !important',
              },
            },
            dateTimeLabelFormats: {
              minute: '%l:%M %p',
            },
            tickPositioner() {
              if (!useEpoch && this.dataMin !== null && this.dataMax !== null) {
                let tick = this.dataMin;
                let totalCount = this.dataMax;
                let increment = Math.ceil(
                  totalCount / MAX_TICK_POSITIONS_TO_SHOW
                );
                let positions = [];

                increment = increment === 0 ? 1 : increment;

                for (tick; tick - increment <= totalCount; tick += increment) {
                  positions.push(tick);
                }

                return positions;
              }
            },
          },
        ],
        yAxis: [
          {
            labels: {
              format: '{value}',
              style: {
                color: 'var(--reset-body-font-color) !important',
              },
            },
            title: undefined,
            id: 'call-count-axis',
          },
        ],
        time: {
          timezoneOffset: this.useEpoch
            ? -moment.tz(this.momentTimezoneId).utcOffset()
            : null,
        },
        credits: false,
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 1450,
              },
              chartOptions: {
                chart: {
                  height: '15%',
                },
              },
            },
            {
              condition: {
                maxWidth: 1080,
              },
              chartOptions: {
                chart: {
                  height: '25%',
                },
              },
            },
            {
              condition: {
                maxWidth: 800,
              },
              chartOptions: {
                chart: {
                  height: '30%',
                },
              },
            },
            {
              condition: {
                maxWidth: 640,
              },
              chartOptions: {
                chart: {
                  height: '60%',
                },
              },
            },
          ],
        },
      };
    },
  },

  watch: {
    showLoader(newValue) {
      this.showChartLoader(newValue);
    },

    momentTimezoneId(newValue) {
      if (this.chart && newValue) {
        this.chart.update({
          time: {
            timezoneOffset: this.useEpoch
              ? -moment.tz(this.momentTimezoneId).utcOffset()
              : null,
          },
        });
      }
    },
  },

  methods: {
    chartLoaded(chart) {
      this.chart = chart;
    },

    showChartLoader(show) {
      if (!this.chart) {
        return;
      }

      if (show) {
        this.chart.showLoading(`Loading data...`);
      } else {
        this.chart.hideLoading();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
</style>